import { trackEvent, useHttpClient } from '@hakimo-ui/hakimo/util';
import { useState } from 'react';

// TTS now goes through our gateway, which holds the ElevenLabs key server-side
// (voice + model are configured in haie_config.yaml). The key is no longer
// shipped to the browser.
const ttsUrl = '/v2/orm/voice/text_to_speech';

type Audio = { audioUrl: string; file: File };

// ponytail: module-level cache keyed by text. Identical talkdown text → identical
// audio → no second request (kills duplicate prefetch/refetch/reopen calls within a
// browser session). Set of distinct strings is tiny, so no eviction.
// Ceiling: per-session only; cross-user dedup would need a server-side cache.
const audioCache = new Map<string, Promise<Audio>>();

/**
 * @param trackingMeta extra Amplitude properties (e.g. source, cameraName) so we
 *   can see where/who is generating talkdown audio and how much.
 */
export function useTextToSpeech(trackingMeta?: Record<string, unknown>) {
  const httpClient = useHttpClient();
  const [isLoading, setIsLoading] = useState(false);

  const fetchAudio = (text: string): Promise<Audio> => {
    const cached = audioCache.get(text);

    trackEvent('generate_text_to_speech', {
      characterCount: text.length,
      cached: Boolean(cached),
      ...trackingMeta,
    });

    if (cached) {
      return cached;
    }

    setIsLoading(true);
    const request = new Request(ttsUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'audio/mpeg',
      },
      body: JSON.stringify({ text }),
    });

    const promise = httpClient<Audio>(request, {
      responseModifier: async (response) => {
        setIsLoading(false);
        const blob = await response.blob();
        const fileUrl = URL.createObjectURL(blob);
        return {
          audioUrl: fileUrl,
          file: new File([blob], Date.now() + '.mpeg'),
        };
      },
    }).catch((err) => {
      // Don't cache failures — let the next attempt retry.
      audioCache.delete(text);
      setIsLoading(false);
      throw err;
    });

    audioCache.set(text, promise);
    return promise;
  };

  return { fetchAudio, isLoading };
}

export default useTextToSpeech;
